// Small helpers shared across the entity detail views (FriendDetailView,
// CategoryDetailView, TripDetailView, TagDetailView). These wrap up the
// "given a list of events, build the year breakdown / top related entities
// / recent events list" patterns so each detail view can stay focused on
// layout.

export const toDate = (value) => {
  if(!value) return null;
  if(value instanceof Date) return value;
  if(typeof value.toDate === 'function') return value.toDate();
  return new Date(value);
};

export const getEventStartAt = (event) => {
  const startAt = toDate(event.startDate);
  if(!startAt) return null;
  const startTime = event.startTime ? toDate(event.startTime) : null;
  if(startTime){
    startAt.setHours(startTime.getHours(), startTime.getMinutes(), 0, 0);
  }
  return startAt;
};

export const formatLongDate = (date) => date.toLocaleDateString('en-US', {
  month: 'long', day: 'numeric', year: 'numeric',
});

export const formatShortMonth = (date) => date.toLocaleDateString('en-US', {
  month: 'short',
});

export const formatYear = (date) => date.getFullYear();

// Returns sorted ascending event start dates (Date objects) for the given
// events, dropping events without a parsable start date.
export const sortedDates = (eventList) => eventList
  .map(getEventStartAt)
  .filter(Boolean)
  .sort((a, b) => a - b);

// Build a year-by-year [year, count] breakdown densely covering the range
// from the earliest to the latest event year. If the list is empty we
// return an empty array so callers can branch.
export const buildYearBreakdown = (eventList) => {
  const dates = sortedDates(eventList);
  if(dates.length === 0) return [];
  const first = dates[0].getFullYear();
  const last = dates[dates.length - 1].getFullYear();
  const counts = {};
  dates.forEach(d => {
    const year = d.getFullYear();
    counts[year] = (counts[year] || 0) + 1;
  });
  const entries = [];
  for(let year = first; year <= last; year++){
    entries.push([year, counts[year] || 0]);
  }
  return entries;
};

// Most-active year [year, count] across the given events, or null.
export const findMostActiveYear = (eventList) => {
  const counts = {};
  eventList.forEach(event => {
    const date = getEventStartAt(event);
    if(!date) return;
    counts[date.getFullYear()] = (counts[date.getFullYear()] || 0) + 1;
  });
  let best = null;
  Object.entries(counts).forEach(entry => {
    if(!best || entry[1] > best[1]) best = entry;
  });
  return best;
};

// Generic "rank by count" helper. Pass a function that returns the related
// id(s) for each event (string or array) and the lookup map of {id: entity}.
// Returns the top N entries as [{entity, count}, ...].
export const rankByEvents = (eventList, getIds, lookup, limit = 6) => {
  const counts = {};
  eventList.forEach(event => {
    const ids = getIds(event);
    if(!ids) return;
    if(Array.isArray(ids)){
      ids.forEach(id => {
        if(!id) return;
        counts[id] = (counts[id] || 0) + 1;
      });
    } else {
      counts[ids] = (counts[ids] || 0) + 1;
    }
  });
  return Object.entries(counts)
    .map(([id, count]) => ({entity: lookup[id], count}))
    .filter(item => item.entity)
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
};

// Most-visited place across the events (by exact location.description),
// returning {description, count} or null.
export const findTopPlace = (eventList) => {
  const counts = {};
  eventList.forEach(event => {
    const description = event.location?.description;
    if(!description) return;
    counts[description] = (counts[description] || 0) + 1;
  });
  let best = null;
  Object.entries(counts).forEach(([description, count]) => {
    if(!best || count > best.count) best = {description, count};
  });
  return best;
};

// Returns events sorted from newest → oldest, capped to `limit`.
export const buildRecentEvents = (eventList, limit = 8) => {
  return [...eventList]
    .map(event => ({event, startAt: getEventStartAt(event)}))
    .filter(item => item.startAt)
    .sort((a, b) => b.startAt - a.startAt)
    .slice(0, limit);
};

// Distinct count of place ids referenced across the events.
export const countDistinctPlaces = (eventList) => {
  const seen = new Set();
  eventList.forEach(event => {
    const id = event.location?.placeId || event.location?.description;
    if(id) seen.add(id);
  });
  return seen.size;
};

// Day-span between the earliest and latest event in the list, inclusive.
// Returns 0 when fewer than 2 dated events exist.
export const computeDaySpan = (eventList) => {
  const dates = sortedDates(eventList);
  if(dates.length < 2) return dates.length;
  const ms = dates[dates.length - 1] - dates[0];
  return Math.max(1, Math.round(ms / (1000 * 60 * 60 * 24)) + 1);
};
